/**
 * The current chat's notice for a past run whose conversation could not be
 * read from the Edge host (agion/Agion#2481). Before it the failure only
 * reached the console, and the run looked as if it said nothing. The new
 * chat supplies its own piece through the chat surface.
 */
import { AlertTriangle, RotateCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@agion/ui'
import type { HistoryFailureProps } from './chatSurface'
import { isUnknownEdgeHost, offlineEdgeHost } from '../../services/api-failure'

export function HistoryLoadNotice({ failure, onRetry }: HistoryFailureProps) {
  const { t } = useTranslation('chat')
  const hostUnknown = isUnknownEdgeHost(failure)
  const offline = offlineEdgeHost(failure)
  return (
    <div
      role="alert"
      data-testid="history-load-notice"
      className="mx-auto flex max-w-2xl items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">{t('customer.historyLoadFailed', 'The conversation could not be loaded from the Edge host.')}</p>
        <p className="mt-0.5 text-amber-800 dark:text-amber-300">
          {hostUnknown
            ? t('customer.historyHostUnknown', 'The Edge host that ran this mission is not recorded, so its conversation cannot be found.')
            : offline
              ? offline.hostName
                ? t('customer.historyHostOfflineNamed', '{{name}}, the Edge host that holds this conversation, is offline. Try again when it is back.', { name: offline.hostName })
                : t('customer.historyHostOffline', 'The Edge host that holds this conversation is offline. Try again when it is back.')
              : t('customer.historyLoadFailedHint', 'The run may have said more than is shown here. Try again in a moment.')}
        </p>
      </div>
      {!hostUnknown && (
        <Button variant="outline" size="sm" onClick={onRetry} className="flex-none gap-1.5">
          <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
          {t('common:retry', 'Retry')}
        </Button>
      )}
    </div>
  )
}
